import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import {
	Fragment,
	MouseEvent as ReactMouseEvent,
	ReactNode,
	useState,
} from 'react';
import {
	ListItem,
	ListItemProps,
	Badge,
	styled,
	BadgeProps,
	Typography,
	Tooltip,
	IconButton,
	Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { ReactComponent as DotIcon } from '@/assets/icon/dot.svg';
import { useGetUser } from '@/hooks/useGetUser';
import { menuPermission } from '@/casl/menuPermission';
import { useRouteMatch } from '@/utils/routeHelper';
import InputIcon from '@mui/icons-material/Input';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { MenuEnum } from '@/api/enum/common.enum';
import { REPORT_PERMISSIONS } from '@/pages/report-summary/ReportSummaryPage';

interface ListItemLinkProps extends ListItemProps {
	icon: ReactNode;
	to: string;
	badgeContent?: number;
	permission?: boolean;
}

const listItemNameMap: { [key: string]: string } = {
	'/pre-admission/pre-admission-list/*':
		'drawerListItems.preAdmissionManagement',
	'/user-management': 'drawerListItems.userManagement',
	'/user-management/user-management-list/*': 'drawerListItems.userManagement',
	'/member-management': 'drawerListItems.memberManagement',
	'/member-management/admission-request-list/*':
		'drawerListItems.admissionRequestSummary',
	'/member-management/pending-submission-list/*':
		'drawerListItems.pendingSubmissionSummary',
	'/member-management/awaiting-list/*': 'drawerListItems.awaitingSummary',
	'/member-management/pending-intake-list/*': 'drawerListItems.pendingSummary',

	'/member-management/member-list/*': 'drawerListItems.memberSummary',

	'/case-management': 'drawerListItems.caseManagement',
	'/case-management/my-case/*': 'drawerListItems.myCaseSummary',
	'/case-management/centre-case/*': 'drawerListItems.centreCaseSummary',
	'/case-management/my-program/*': 'drawerListItems.myProgramMemberSummary',
	'/case-management/centre-program/*':
		'drawerListItems.centreProgramMemberSummary',
	'/case-management/intervention/*': 'drawerListItems.myInterventionSummary',
	'/case-management/centre-intervention/*':
		'drawerListItems.centreInterventionSummary',

	'/family-management': 'drawerListItems.familyMemberManagement',
	'/family-management/family-management-list/*':
		'drawerListItems.familyMemberSummary',

	'/field-management': 'drawerListItems.FieldOptionManagement',
	'/field-management/field-management-list/*':
		'drawerListItems.FieldOptionSummary',

	'/report-summary': 'drawerListItems.reportSummary',
};
interface rootMenu {
	permission: boolean;
	icon: ReactNode;
	to: string;
	nestedCollapseList?: NestedMenuItem[];
}
interface NestedMenuItem {
	permission: boolean;
	to: string;
	badgeContent?: number;
}

interface ListItemsProps {
	drawerOpen: boolean;
	handleDrawerOpen: () => void;
	scrollMainToTop: () => void;
}

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
	'& .MuiBadge-badge': {
		position: 'relative',
		top: 10,
		backgroundColor: '#FFDAD6',
		color: '#FF5449',
	},
}));

export default function ListItems({
	drawerOpen,
	handleDrawerOpen,
	scrollMainToTop,
}: ListItemsProps) {
	const { userInfo } = useGetUser();

	const { t } = useTranslation();
	const { pathname } = useLocation();

	const routeMatch = useRouteMatch(pathname, Object.keys(listItemNameMap));
	const currentTab = routeMatch?.pattern?.path;

	// menus are expanded by default, so we only track the ones the user collapsed
	const [collapsedIndex, setCollapsedIndex] = useState<number[]>([]);
	const isMenuOpen = (index: number) => !collapsedIndex.includes(index);

	const rootMenus: rootMenu[] = [
		...(userInfo?.menuList?.includes(MenuEnum.PreAdmissionManagement)
			? [
					{
						permission: true,
						icon: <InboxOutlinedIcon />,
						to: '/pre-admission/pre-admission-list',
					},
				]
			: []),
		...(userInfo?.menuList?.includes(MenuEnum.MemberManagement)
			? [
					{
						permission: true,
						icon: <AssignmentIndOutlinedIcon />,
						to: '/member-management',
						nestedCollapseList: [
							...(userInfo?.menuList?.includes(MenuEnum.MemberSummary)
								? [
										{
											permission: true,
											to: '/member-management/member-list',
										},
									]
								: []),
							...(userInfo?.menuList?.includes(MenuEnum.AdmissionRequestSummary)
								? [
										{
											permission: true,
											to: '/member-management/admission-request-list',
										},
									]
								: []),
							...(userInfo?.menuList?.includes(
								MenuEnum.PendingSubmissionSummary
							)
								? [
										{
											permission: true,
											to: '/member-management/pending-submission-list',
										},
									]
								: []),
							...(userInfo?.menuList?.includes(
								MenuEnum.AwaitingAssignmentSummary
							)
								? [
										{
											permission: true,
											to: '/member-management/awaiting-list',
										},
									]
								: []),
							...(userInfo?.menuList?.includes(MenuEnum.PendingIntakeSummary)
								? [
										{
											permission: true,
											to: '/member-management/pending-intake-list',
										},
									]
								: []),
						],
					},
				]
			: []),

		...(userInfo?.menuList?.includes(MenuEnum.CaseManagement)
			? [
					{
						permission: true,
						icon: <FolderCopyOutlinedIcon />,
						to: '/case-management',
						nestedCollapseList: [
							...(userInfo?.menuList?.includes(MenuEnum.MyCaseSummary)
								? [
										{
											permission: true,
											to: '/case-management/my-case',
										},
									]
								: []),
							...(userInfo?.menuList?.includes(MenuEnum.CentreCaseSummary)
								? [
										{
											permission: true,
											to: '/case-management/centre-case',
										},
									]
								: []),
							...(userInfo?.menuList?.includes(MenuEnum.MyProgramMemberSummary)
								? [
										{
											permission: true,
											to: '/case-management/my-program',
										},
									]
								: []),
							...(userInfo?.menuList?.includes(
								MenuEnum.CentreProgramMemberSummary
							)
								? [
										{
											permission: true,
											to: '/case-management/centre-program',
										},
									]
								: []),
							...(userInfo?.menuList?.includes(MenuEnum.MyInterventionSummary)
								? [
										{
											permission: true,
											to: '/case-management/intervention',
										},
									]
								: []),
							...(userInfo?.menuList?.includes(
								MenuEnum.CentreInterventionSummary
							)
								? [
										{
											permission: true,
											to: '/case-management/centre-intervention',
										},
									]
								: []),
						],
					},
				]
			: []),

		...(userInfo?.menuList?.includes(MenuEnum.SystemUserManagement)
			? [
					{
						permission: true,
						icon: <PersonOutlineIcon />,
						to: '/user-management/user-management-list',
					},
				]
			: []),
		...(userInfo?.menuList?.includes(MenuEnum.FamilyMemberSummary)
			? [
					{
						permission: true,
						icon: <Groups2OutlinedIcon />,
						to: '/family-management/family-management-list',
					},
				]
			: []),
		...(userInfo?.menuList?.includes(MenuEnum.FieldOptionsSummary)
			? [
					{
						permission: true,
						icon: <TuneOutlinedIcon />,
						to: '/field-management/field-management-list',
					},
				]
			: []),
		...(Object.values(REPORT_PERMISSIONS).some((permission) =>
			userInfo?.permissionList?.includes(permission)
		)
			? [
					{
						permission: true,
						icon: <SummarizeOutlinedIcon />,
						to: '/report-summary',
					},
				]
			: []),
	];

	// already on the target route: skip the navigation and scroll the page up
	const handleMenuLinkClick = (
		event: ReactMouseEvent<HTMLElement>,
		to: string
	) => {
		if (pathname !== to) return;
		event.preventDefault();
		scrollMainToTop();
	};

	const handleOpenIndex = (index: number) => {
		handleDrawerOpen();
		if (drawerOpen && isMenuOpen(index)) {
			setCollapsedIndex((oldValues) => [...oldValues, index]);
		} else if (drawerOpen) {
			setCollapsedIndex((oldValues) => {
				return oldValues.filter((value) => value !== index);
			});
		} else {
			// re-opening the drawer collapses everything but the clicked menu
			setCollapsedIndex(rootMenus.map((_, i) => i).filter((i) => i !== index));
		}
	};

	function SingleListItem(props: ListItemLinkProps) {
		const { icon, to, sx, badgeContent, permission } = props;
		const pathMatch =
			useRouteMatch(to, Object.keys(listItemNameMap))?.pattern.path ?? '';
		const primary = listItemNameMap[pathMatch];

		const listItemButtonProps = { component: Link, to: to };

		if (permission && menuPermission[userInfo?.role]) {
			const permission = menuPermission[userInfo?.role].find((item: string) => {
				if (
					item === to ||
					(item.endsWith('/*') && to.startsWith(item.slice(0, -2)))
				) {
					return true;
				} else {
					return false;
				}
			});
			if (!permission) return <></>;
		}

		return (
			<>
				<ListItem component='li' disablePadding>
					<ListItemButton
						{...listItemButtonProps}
						onClick={(event) => handleMenuLinkClick(event, to)}
						selected={currentTab == pathMatch}
						sx={{
							justifyContent: drawerOpen ? 'initial' : 'center',
							px: 2.5,
							...sx,
							backgroundColor: 'transparent!important',
						}}
					>
						{/* <ListItemIcon
							sx={{
								minWidth: 0,
								mr: drawerOpen ? 3 : 'auto',
								justifyContent: 'center',
								alignItems: 'center',
								flexDirection: 'column',
								color:
									currentTab == pathMatch ? 'chipGreen.main' : 'text.primary',
							}}
						>
							{icon}
							{!drawerOpen && (
								<Typography
									variant='subtitle1'
									style={{
										maxWidth: '60px',
										textAlign: 'center',
										whiteSpace: 'normal',
										display: '-webkit-box',
										WebkitLineClamp: 2,
										WebkitBoxOrient: 'vertical',
										overflow: 'hidden',
									}}
								>
									{t(primary)}
								</Typography>
							)}
						</ListItemIcon> */}
						<ListItemText
							primary={t(primary)}
							sx={{
								paddingLeft: '20px',
								whiteSpace: 'normal',
								display: '-webkit-box',
								WebkitLineClamp: 2,
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden',
								opacity: drawerOpen ? 1 : 0,
								color:
									currentTab == pathMatch ? 'chipGreen.main' : 'text.primary',
							}}
						/>

						{badgeContent !== undefined && badgeContent > 0 && (
							<StyledBadge badgeContent={badgeContent}></StyledBadge>
						)}
					</ListItemButton>
				</ListItem>
			</>
		);
	}

	return (
		<List>
			{rootMenus.map((menu, index) => {
				const pathMatch =
					useRouteMatch(menu.to, Object.keys(listItemNameMap))?.pattern.path ??
					'';
				const primary = listItemNameMap[pathMatch];
				if (menu.permission && menuPermission[userInfo?.role]) {
					const permission = menuPermission[userInfo?.role].find(
						(item: string) => {
							if (
								item === menu.to ||
								(item.endsWith('/*') && menu.to.startsWith(item.slice(0, -2)))
							) {
								return true;
							} else {
								return false;
							}
						}
					);
					if (!permission) return;
				}

				const listItemButtonProps =
					!menu.nestedCollapseList || menu.nestedCollapseList.length < 1
						? { component: Link, to: menu.to }
						: {};

				return (
					<Fragment key={index}>
						<ListItem
							component='li'
							onClick={() => handleOpenIndex(index)}
							disablePadding
						>
							<ListItemButton
								sx={{
									justifyContent: drawerOpen ? 'initial' : 'center',
									px: 1,
								}}
								{...listItemButtonProps}
								onClick={(event) => handleMenuLinkClick(event, menu.to)}
							>
								<ListItemIcon
									sx={{
										minWidth: 0,
										mr: drawerOpen ? 1 : '0',
										justifyContent: 'center',
										alignItems: 'center',
										// flexDirection: 'column',
										color:
											currentTab == pathMatch
												? 'chipGreen.main'
												: 'text.primary',
									}}
								>
									{drawerOpen && (
										<Badge
											sx={{
												'& .MuiBadge-badge': {
													backgroundColor: '#FF5449',
												},
											}}
											variant='dot'
											invisible={
												!menu.nestedCollapseList?.some(
													(value: { to: string; badgeContent?: number }) => {
														return (
															value.badgeContent !== undefined &&
															value.badgeContent > 0
														);
													}
												)
											}
										>
											{menu.icon}
										</Badge>
									)}
									{!drawerOpen && (
										<Tooltip title={t(primary)}>
											<IconButton>{menu.icon}</IconButton>
										</Tooltip>
									)}
								</ListItemIcon>
								{drawerOpen && (
									<ListItemText
										primary={t(primary)}
										primaryTypographyProps={{
											sx: {
												whiteSpace: 'normal',
												wordWrap: 'break-word',
												overflow: 'visible',
												textOverflow: 'clip',
											},
										}}
										sx={{
											opacity: drawerOpen ? 1 : 0,
											color:
												currentTab == pathMatch
													? 'chipGreen.main'
													: 'text.primary',
										}}
									/>
								)}

								{Object.keys(listItemButtonProps).length < 1 &&
									drawerOpen &&
									(isMenuOpen(index) ? <ExpandLess /> : <ExpandMore />)}
							</ListItemButton>
						</ListItem>
						{menu.nestedCollapseList && drawerOpen && (
							<Collapse
								component='li'
								in={isMenuOpen(index)}
								timeout='auto'
								unmountOnExit
							>
								<List disablePadding>
									{menu.nestedCollapseList.map((subMenu, subIndex) => (
										<SingleListItem
											key={subIndex}
											icon={<DotIcon />}
											to={subMenu.to}
											badgeContent={subMenu.badgeContent}
											permission={subMenu.permission}
										/>
									))}
								</List>
							</Collapse>
						)}
					</Fragment>
				);
			})}
		</List>
	);
}
